import { createTheme } from '@mui/material/styles';

// Azure Portal-aligned color palette — Azure blue #0078d4
const lightColors = {
  primary: {
    main: '#0078d4',
    light: '#1a8cff',
    dark: '#106ebe',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#0c2d48',
    light: '#1a4a6e',
    dark: '#071d30',
    contrastText: '#ffffff',
  },
  background: {
    default: '#f3f2f1',
    paper: '#ffffff',
    sidebar: '#ffffff',
    navbar: '#0c2d48',
    card: '#ffffff',
  },
  text: {
    primary: '#201f1e',
    secondary: '#605e5c',
    disabled: '#a19f9d',
  },
  divider: '#edebe9',
  border: '#edebe9',
  success: {
    main: '#12B76A',
    light: '#32D583',
    dark: '#039855',
  },
  warning: {
    main: '#F79009',
    light: '#FDB022',
    dark: '#DC6803',
  },
  error: {
    main: '#F04438',
    light: '#F97066',
    dark: '#D92D20',
  },
  info: {
    main: '#0BA5EC',
    light: '#36BFFA',
    dark: '#0086C9',
  },
};

const darkColors = {
  primary: {
    main: '#465FFF',
    light: '#6B7FFF',
    dark: '#3347DB',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#8A99AF',
    light: '#C8CFD8',
    dark: '#5B6B82',
    contrastText: '#000000',
  },
  background: {
    default: '#1A2232',
    paper: '#24303F',
    sidebar: '#1C2434',
    navbar: '#24303F',
    card: '#24303F',
  },
  text: {
    primary: '#DEE4EE',
    secondary: '#8A99AF',
    disabled: '#5B6B82',
  },
  divider: '#2E3A4B',
  border: '#2E3A4B',
  success: {
    main: '#34D399',
    light: '#6CE9A6',
    dark: '#12B76A',
  },
  warning: {
    main: '#FBBF24',
    light: '#FDE68A',
    dark: '#F79009',
  },
  error: {
    main: '#F97066',
    light: '#FDA29B',
    dark: '#F04438',
  },
  info: {
    main: '#36BFFA',
    light: '#7CD4FD',
    dark: '#0BA5EC',
  },
};

// Create light theme
export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    ...lightColors,
  },
  typography: {
    fontFamily: [
      '"Segoe UI"',
      '-apple-system',
      'BlinkMacSystemFont',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: { fontSize: '2rem', fontWeight: 700, lineHeight: 1.2 },
    h2: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.3 },
    h3: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.3 },
    h4: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
    h5: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
    h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 },
    body1: { fontSize: '0.875rem', lineHeight: 1.5 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
    subtitle1: { fontSize: '0.9375rem', fontWeight: 500 },
    subtitle2: { fontSize: '0.8125rem', fontWeight: 500 },
    caption: { fontSize: '0.75rem', lineHeight: 1.5 },
    button: { fontSize: '0.875rem', fontWeight: 500, textTransform: 'none' },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: lightColors.background.default,
          color: lightColors.text.primary,
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: 12,
          border: `1px solid ${lightColors.border}`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: lightColors.background.card,
          borderRadius: 12,
          border: `1px solid ${lightColors.border}`,
          boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.06), 0px 1px 3px 0px rgba(16, 24, 40, 0.10)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 500, fontSize: '0.75rem' },
      },
    },
  },
});

// Create dark theme
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    ...darkColors,
  },
  typography: {
    fontFamily: [
      'Outfit',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: { fontSize: '2rem', fontWeight: 700, lineHeight: 1.2, color: darkColors.text.primary },
    h2: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.3, color: darkColors.text.primary },
    h3: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.3, color: darkColors.text.primary },
    h4: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4, color: darkColors.text.primary },
    h5: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4, color: darkColors.text.primary },
    h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4, color: darkColors.text.primary },
    body1: { fontSize: '0.875rem', lineHeight: 1.5, color: darkColors.text.primary },
    body2: { fontSize: '0.8125rem', lineHeight: 1.5, color: darkColors.text.secondary },
    subtitle1: { fontSize: '0.9375rem', fontWeight: 500 },
    subtitle2: { fontSize: '0.8125rem', fontWeight: 500 },
    caption: { fontSize: '0.75rem', lineHeight: 1.5 },
    button: { fontSize: '0.875rem', fontWeight: 500, textTransform: 'none' },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: darkColors.background.default,
          color: darkColors.text.primary,
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: 12,
          border: `1px solid ${darkColors.border}`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: darkColors.background.card,
          borderRadius: 12,
          border: `1px solid ${darkColors.border}`,
          boxShadow: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 500, fontSize: '0.75rem' },
      },
    },
  },
});

// Theme config object for ThemeProvider
export const themeConfig = {
  light: lightTheme,
  dark: darkTheme,
};

// Export color tokens for direct use in components
export { lightColors, darkColors };
